import * as React from 'react'
import * as cn from 'classnames'
import * as csm from '../../../utils/csm'
import fireDraperTrackingEvent from '../../../utils/draper'
import * as styles from './PreRollPlayer.module.scss'
import { mutedButton, unmutedButton } from './MuteActionImages'
import fetch from '../../../utils/fetch'
import { HeroElement } from './HeroElement';
import { StorefrontItem } from 'DVWebNodeUtils';

const LOAD_ERROR_TIMEOUT = 4000
const PLAYER_COUNTER_PREFIX = 'DVWeb::Storefront::PreRollPlayer'

interface PreRollPlayerProps {
  item: StorefrontItem
  autoScrollFunc: Function
  isActive: boolean
  autoScrollDelayMilli: number
  prerollMuteAction: Function
}

interface PreRollPlayerState {
  playing: boolean
  originalVolume?: number
  hasPlayed: boolean
  hasError: boolean
  muteWrapperStyle: string
  mutedStyle: string
  unmutedStyle: string
  defaultVolume?: number
}

export default class PreRollPlayer extends React.PureComponent<PreRollPlayerProps, PreRollPlayerState> {
  playHandler: () => void
  stopHandler: () => void
  updateHandler: () => void
  errorHandler: () => void
  waitingHandler: () => void
  firstQuartileReached: boolean
  midpointReached: boolean
  thirdQuartileReached: boolean
  expectedReadyState: number
  isMuted: boolean

  wrappers: {
    videoWrapper?: HTMLAnchorElement
    videoPlayer?: HTMLVideoElement
  } = {}

  constructor(props: PreRollPlayerProps) {

    super(props)
    this.state = {
      hasError: false,
      playing: false,
      muteWrapperStyle: styles.hide,
      hasPlayed: false,
      mutedStyle: styles.hideMuted,
      unmutedStyle: styles.hideUnmuted,
    }
    this.playHandler = null
    this.stopHandler = null
    this.updateHandler = null

    this.firstQuartileReached = false
    this.midpointReached = false
    this.thirdQuartileReached = false
    this.expectedReadyState = 4
    this.isMuted = true
  }

  componentDidMount(): void {
    this.setState({ originalVolume: this.wrappers.videoPlayer.volume })
    this.wrappers.videoPlayer.preload = 'auto'
  }

  setUpPlay(heroComponent: HeroElement) {
    heroComponent.components.playerHoverComponent.playerVisible(true)
    heroComponent.components.playerHoverComponent.toggleOverlay(true)
    csm.count(`${PLAYER_COUNTER_PREFIX}::BUFFER::INITIALISED`)
    this.setState({ ...this.state, playing: true, hasPlayed: true, muteWrapperStyle: styles.show })
  }

  play(heroComponent: HeroElement) {
    if (this.wrappers.videoPlayer && this.wrappers.videoPlayer.readyState >=
      this.expectedReadyState) {
      const playPromise = this.wrappers.videoPlayer.play()
      if (typeof playPromise !== 'undefined') {
        playPromise.then(() => {
          this.setUpPlay(heroComponent)
          fireDraperTrackingEvent(this.props.item, 'defaultImpression')
        }).catch((err: Error) => {
          fireDraperTrackingEvent(this.props.item, 'error')
          csm.logError(`Pre-Roll Player Failed to play: ${err.message}.`, err)
        })
      } else { // This is for IE11 and below as it does not support Promise's https://caniuse.com/#feat=promises
        try {
          this.wrappers.videoPlayer.play()
          this.setUpPlay(heroComponent)
          fireDraperTrackingEvent(this.props.item, 'defaultImpression')
        } catch (err) {
          fireDraperTrackingEvent(this.props.item, 'error')
          csm.logError(`Pre-Roll Player Failed to play: ${err.message}.`, err)
        }
      }
    }
  }

  onLoadError(heroComponent: HeroElement) {
    fireDraperTrackingEvent(this.props.item, 'error')
    csm.count(`${PLAYER_COUNTER_PREFIX}::loadError`)

    setTimeout(() => {
      if (this.props.isActive) {
        this.props.autoScrollFunc(this.props.autoScrollDelayMilli)
      }
      this.stop(heroComponent)
    }, LOAD_ERROR_TIMEOUT)
  }

  onTimeUpdate() {
    const duration = this.wrappers.videoPlayer && this.wrappers.videoPlayer.duration,
      currentTime = this.wrappers.videoPlayer && this.wrappers.videoPlayer.currentTime
    if (!duration || !currentTime) {
      return
    }


    if (!this.thirdQuartileReached && currentTime >= duration * 0.75) {
      this.thirdQuartileReached = true
      fireDraperTrackingEvent(this.props.item, 'thirdQuartile')
    } else if (!this.midpointReached && currentTime >= duration * 0.5) {
      this.midpointReached = true
      fireDraperTrackingEvent(this.props.item, 'midpoint')
    } else if (!this.firstQuartileReached && currentTime >= duration * 0.25) {
      this.firstQuartileReached = true
      fireDraperTrackingEvent(this.props.item, 'firstQuartile')
    }

  }

  onEnded(heroComponent: HeroElement) {
    if (this.props.isActive) {
      this.props.autoScrollFunc(this.props.autoScrollDelayMilli)
    }
    fireDraperTrackingEvent(this.props.item, 'complete')
    return this.stop(heroComponent)
  }

  stop(heroComponent: HeroElement) {
    this.setState({
      playing: false, hasPlayed: false, muteWrapperStyle: styles.hide,
      mutedStyle: styles.hideMuted, unmutedStyle: styles.hideUnmuted
    })
    heroComponent.components.playerHoverComponent.playerVisible(false)
    heroComponent.components.playerHoverComponent.toggleOverlay(false)

    this.wrappers.videoPlayer.pause()

    this.removeEventListeners()
  }

  logMuteMetrics() {
    if (this.wrappers.videoPlayer.muted) {
      fireDraperTrackingEvent(this.props.item, 'mute')
      fetch(this.props.item.prerolls.ipbTrackingEvents.mute)
    } else {
      fireDraperTrackingEvent(this.props.item, 'unmute')
      fetch(this.props.item.prerolls.ipbTrackingEvents.unmute)
    }
  }

  toggleMute(event: { preventDefault: () => void; }) {
    event.preventDefault()
    if (this.wrappers.videoPlayer.muted) {
      this.wrappers.videoPlayer.muted = false
      this.wrappers.videoPlayer.volume = this.state.originalVolume > 0 ? this.state.originalVolume
        : this.state.defaultVolume
      this.setState({
        mutedStyle: styles.hideMuted,
        unmutedStyle: styles.showUnmuted
      })
    } else {
      this.wrappers.videoPlayer.muted = true
      this.wrappers.videoPlayer.volume = 0
      this.setState({
        mutedStyle: styles.showMuted,
        unmutedStyle: styles.hideUnmuted
      })
    }
    this.props.prerollMuteAction(this.wrappers.videoPlayer.muted)
    this.logMuteMetrics()
  }


  onWaiting() {
    csm.count(`${PLAYER_COUNTER_PREFIX}::BUFFER::REINITIALISING`)
  }

  addEventListeners() {
    this.wrappers.videoPlayer.addEventListener('canplaythrough', this.playHandler)
    this.wrappers.videoPlayer.addEventListener('ended', this.stopHandler)
    this.wrappers.videoPlayer.addEventListener('timeupdate', this.updateHandler)
    this.wrappers.videoPlayer.addEventListener('waiting', this.waitingHandler)
    this.wrappers.videoPlayer.addEventListener('error', this.errorHandler)
  }

  removeEventListeners() {
    this.stopHandler &&
      this.wrappers.videoPlayer.removeEventListener('ended', this.stopHandler)
    this.playHandler &&
      this.wrappers.videoPlayer.removeEventListener('canplaythrough', this.playHandler)
    this.updateHandler &&
      this.wrappers.videoPlayer.removeEventListener('timeupdate', this.updateHandler)
    this.errorHandler &&
      this.wrappers.videoPlayer.removeEventListener('error', this.errorHandler)
    this.waitingHandler &&
      this.wrappers.videoPlayer.removeEventListener('waiting', this.waitingHandler)
  }

  componentWillUnmount() {
    this.removeEventListeners()
  }

  initialiseMuteAction() {
    this.wrappers.videoPlayer.muted = this.isMuted
    this.wrappers.videoPlayer.volume = this.isMuted ? 0
      : this.wrappers.videoPlayer.volume
    this.setState({
      ...this.state,
      mutedStyle: this.isMuted ? styles.showMuted : styles.hideMuted,
      unmutedStyle: this.isMuted ? styles.hideUnmuted : styles.showUnmuted
    })
  }

  startPlayer(heroComponent: any, isMuted: boolean) {
    this.isMuted = isMuted
    this.initialiseMuteAction()
    this.wrappers.videoPlayer.load()
    this.playHandler = () => this.play(heroComponent)
    this.stopHandler = () => this.onEnded(heroComponent)
    this.updateHandler = () => this.onTimeUpdate()
    this.errorHandler = () => this.onLoadError(heroComponent)
    this.waitingHandler = () => this.onWaiting()
    this.addEventListeners()
    // Sometimes in Firefox when the video is in the cache of the browser
    // the canplaythrough event is fired before we have had a chance to
    // register the event
    if (this.wrappers.videoPlayer.readyState >= this.expectedReadyState) {
      this.playHandler()
    }
  }

  render() {
    if (this.state.hasError || !this.props.item.prerolls || !this.props.item.prerolls.mediaFile) {
      return null
    }
    const showMuteToggle = !this.props.item.prerolls.disableUnmute
    return (
      <a className={cn(styles.preRollPlayer, this.state.muteWrapperStyle, 'tst-video-overlay-player-overlay', 'tst-preroll-player')}
        href={this.props.item.link.url} ref={
          (elem) => {
            this.wrappers.videoWrapper = elem
          }
        }>
        <video
          className={'tst-video-overlay-player-html5'}
          src={this.props.item.prerolls.mediaFile} ref={
            (elem) => {
              this.wrappers.videoPlayer = elem
            }}
        />
        <div className={styles.videoFadeOverlay} />
        {showMuteToggle && <a onClick={this.toggleMute.bind(this)}
          className={cn(styles.mutingAction, this.state.mutedStyle, 'tst-video-overlay-player-muted')}>{mutedButton}</a>}
        {showMuteToggle && <a onClick={this.toggleMute.bind(this)}
          className={cn(styles.mutingAction, this.state.unmutedStyle, 'tst-video-overlay-player-unmuted')}>{unmutedButton}</a>}
      </a>

    )
  }
}
